import React, { useState } from 'react';
import { Heart } from 'lucide-react';
import { TabBarItem } from '@ajat/design-system/Configurator';
import { ComponentPage, DemoCard, DemoRow, Demo } from '../../shared';

/* Overall configurator step icons (Figma custom/24/*-v2, MCP Version 4467:1572–1576) */
const TextIcon = require('@ajat/design-system/assets/overall/configurator/icons/icon-text.svg').ReactComponent;
const PatchIcon = require('@ajat/design-system/assets/overall/configurator/icons/icon-patch.svg').ReactComponent;
const QrCodeIcon = require('@ajat/design-system/assets/overall/configurator/icons/icon-qr-code.svg').ReactComponent;
/* Crown Student cap-part icons — wide artwork drawn through the icon size hooks */
const MosskulleIcon = require('@ajat/design-system/assets/crownstudent/configurator/steps/mosskulle.svg').ReactComponent;
const StrimmaIcon = require('@ajat/design-system/assets/crownstudent/configurator/steps/strimma.svg').ReactComponent;

export const meta = {
  component: TabBarItem,
  title: 'Tab Bar Item',
  group: 'Configurator',
  importPath: '@ajat/design-system/Configurator',
  figmaNode: '4173:4482',
  description:
    'Icon-over-label step tab for the configurator rail (Text / Universe / QR / …), also used for single actions such as "Spara". The active tab sits on a white tile; `done` adds the check to the left of the icon. It renders a plain button — pass `role="tab"` + `aria-selected` for a rail, `aria-pressed` for a toggle. Responsive like the Figma Resolution variants (Brand=Overall): below 1024px a pill with a 10px label (the mobile bottom bar), from 1024px a 72px tile with a 12px label (the desktop rail). Without a label it is icon-only. Wide artwork (the Crown Student cap-part icons) sizes the 24px icon slot through the CSS hooks `--cfg-tab-bar-item-icon-w` / `-h`, set on the consumer\'s container.',
  props: [
    ['icon', 'node', '—', '24px icon (SVG component, <img> or lucide icon)'],
    ['label', 'string', '—', 'Label under the icon (P.Label mobile / P.Small desktop); omit for icon-only'],
    ['active', 'bool', 'false', 'Active state — white tile'],
    ['done', 'bool', 'false', 'Finished step — check to the left of the icon'],
    ['compact', 'bool', 'false', 'Compact 40px icon-only form — the label folds away with a transition'],
    ['onClick', 'func', '—', 'Click handler'],
    ['className', 'string', "''", 'Additional CSS class'],
  ],
  usage: [
    {
      title: 'Step rail',
      code: `import { TabBarItem } from '@ajat/design-system/Configurator';

<div role="tablist" aria-orientation="vertical">
  {TABS.map((tab) => (
    <TabBarItem
      key={tab.id}
      role="tab"
      aria-selected={tab.id === activeTab}
      icon={<tab.Icon />}
      label={tab.label}
      active={tab.id === activeTab}
      done={completedTabs.has(tab.id)}
      onClick={() => setActiveTab(tab.id)}
    />
  ))}
</div>`,
    },
  ],
};

/* Tab bar items sit on the rail's gray — a white Selected tile vanishes on the white demo card */
const RAIL_STYLE = {
  display: 'inline-flex',
  padding: 'var(--space-ui-small-s)',
  background: 'var(--color-gray-075)',
  borderRadius: 'var(--radius-popup)',
};

const DEMO_TABS = [
  { id: 'text', label: 'Text', Icon: (props) => <TextIcon {...props} /> },
  { id: 'vanliga', label: 'Universe', Icon: PatchIcon },
  { id: 'qr', label: 'QR', Icon: QrCodeIcon },
];

export default function TabBarItemPage() {
  const [active, setActive] = useState('vanliga');

  return (
    <ComponentPage meta={meta}>
      <DemoCard title="States" hint="hover for the Hover state">
        <DemoRow align="center">
          <Demo caption="DEFAULT" center><span style={RAIL_STYLE}><TabBarItem icon={<TextIcon />} label="Text" /></span></Demo>
          <Demo caption="SELECTED" center><span style={RAIL_STYLE}><TabBarItem icon={<TextIcon />} label="Text" active /></span></Demo>
          <Demo caption="DONE" center><span style={RAIL_STYLE}><TabBarItem icon={<TextIcon />} label="Text" done /></span></Demo>
          <Demo caption="COMPACT" center><span style={RAIL_STYLE}><TabBarItem icon={<TextIcon />} label="Text" done compact /></span></Demo>
          <Demo caption="ACTION" center><TabBarItem icon={<Heart size={24} strokeWidth={1.75} />} label="Spara" /></Demo>
        </DemoRow>
      </DemoCard>

      <DemoCard title="Wide artwork" hint="icon-only tabs; the container sets --cfg-tab-bar-item-icon-w / -h">
        <div
          role="tablist"
          className="flex gap-small-s"
          style={{ ...RAIL_STYLE, '--cfg-tab-bar-item-icon-w': '50.4px', '--cfg-tab-bar-item-icon-h': '36px' }}
        >
          <TabBarItem role="tab" aria-selected aria-label="Mösskulle" icon={<MosskulleIcon width="100%" height="100%" />} active />
          <TabBarItem role="tab" aria-selected={false} aria-label="Strimma" icon={<StrimmaIcon width="100%" height="100%" />} />
        </div>
      </DemoCard>

      <DemoCard title="Step rail" hint="click a tab — the one you leave is marked done">
        <div
          role="tablist"
          aria-orientation="vertical"
          className="flex-col gap-small-s"
          style={RAIL_STYLE}
        >
          {DEMO_TABS.map(({ id, label, Icon }) => (
            <TabBarItem
              key={id}
              role="tab"
              aria-selected={id === active}
              icon={<Icon />}
              label={label}
              active={id === active}
              done={id === 'text' && active !== 'text'}
              onClick={() => setActive(id)}
            />
          ))}
        </div>
      </DemoCard>
    </ComponentPage>
  );
}
